import dayjs from 'dayjs';
import { formatCompactDuration, secondsSince } from '@/utils/checkinStatus';
import { formatSyncTime } from '@/utils/syncStatus';
import {
    DAY_TEXT_LABELS,
    formatClockTime,
    formatScheduleDay,
    formatScheduleShortDate,
    formatTimeRange,
} from '@/utils/customPlaylistSchedule';
import {
    PLAYLIST_GENERATION_STATUS,
    type DailyListReason,
    type PlayerScheduleReport,
    type PlayerScheduleRow,
    type PlayerScheduleRun,
} from '@/composables/api/useLocationZonePlayerScheduleApi';

/**
 * Pure logic behind the player inspector's Schedule tab: the progress bar of a
 * reload or a generate, the status line, and the daily range summary. Nothing
 * here reads the clock or the network, so all of it is unit tested.
 */

export type ScheduleStepKey = 'generating' | 'sent' | 'received' | 'downloading' | 'up_to_date' | 'reported';

export type ScheduleStepStatus = 'done' | 'current' | 'pending' | 'failed';

export interface ScheduleStep {
    key: ScheduleStepKey;
    label: string;
    status: ScheduleStepStatus;
}

export interface ScheduleProgress {
    kind: PlayerScheduleRun['kind'];
    steps: ScheduleStep[];
    /** The first step with no evidence yet. Null once every step is done. */
    currentStep: ScheduleStepKey | null;
    /** The one line under the bar. */
    detail: string;
    /** The generation attempt ended failed. */
    failed: boolean;
    /** No new report 15 minutes after the run started. */
    timedOut: boolean;
    /** Still being followed: the button stays disabled and the tab keeps asking. */
    running: boolean;
}

export type ScheduleProgressInput = Pick<PlayerScheduleReport, 'report' | 'run'>;

export interface ScheduleProgressOptions {
    /** The player's last check-in, for "checked in 9s ago". */
    lastCheckinAt?: string | null;
    /**
     * The server has stopped returning this run without a newer report. It does
     * that 15 minutes after the run started, so the run timed out, even if this
     * browser's clock says otherwise.
     */
    serverEnded?: boolean;
}

const STEP_LABELS: Record<ScheduleStepKey, string> = {
    generating: 'Generating',
    sent: 'Sent',
    received: 'Received',
    downloading: 'Downloading',
    up_to_date: 'Up to date',
    reported: 'Reported',
};

const RELOAD_STEPS: ScheduleStepKey[] = ['sent', 'received', 'downloading', 'up_to_date', 'reported'];
const GENERATE_STEPS: ScheduleStepKey[] = ['generating', ...RELOAD_STEPS];

function timeMs(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
}

/** "2:14 PM". A run lasts at most 15 minutes, so the day is never needed. */
function clock(value: string | null | undefined): string {
    const moment = value ? dayjs(value) : null;
    return moment && moment.isValid() ? moment.format('h:mm A') : '';
}

/** "ADD" for "ADD#https://…/archive.tar.gz": the archive address means nothing to a reader. */
function commandVerb(run: PlayerScheduleRun): string {
    const command = run.command?.command ?? (run.kind === 'reload' ? 'ADDCUSTOM' : 'ADD');
    return command.split('#')[0] || command;
}

/** Whether the step at this key has evidence of its own. */
function hasEvidence(key: ScheduleStepKey, input: ScheduleProgressInput, run: PlayerScheduleRun): boolean {
    switch (key) {
        case 'generating':
            return run.attempt?.playlist_generation_status_id === PLAYLIST_GENERATION_STATUS.COMPLETED;
        case 'sent':
            return Boolean(run.command?.issued_at);
        case 'received':
            return Boolean(run.command?.acknowledged_at);
        case 'downloading':
            return Boolean(run.command?.delivered_at);
        case 'up_to_date':
            return Boolean(run.command?.completed_at);
        case 'reported': {
            const reportedMs = timeMs(input.report.reported_at);
            const startedMs = timeMs(run.started_at);
            return reportedMs !== null && startedMs !== null && reportedMs > startedMs;
        }
    }
}

function checkedInSuffix(lastCheckinAt: string | null | undefined, nowMs: number): string {
    const seconds = secondsSince(lastCheckinAt ?? null, nowMs);
    return seconds === null ? '' : ` · checked in ${formatCompactDuration(seconds)} ago`;
}

function runningDetail(
    current: ScheduleStepKey,
    run: PlayerScheduleRun,
    nowMs: number,
    lastCheckinAt: string | null | undefined
): string {
    const verb = commandVerb(run);
    const checkedIn = checkedInSuffix(lastCheckinAt, nowMs);
    switch (current) {
        case 'generating':
            return `Generating daily lists · started ${clock(run.started_at)}`;
        case 'sent': {
            if (run.kind === 'generate') {
                return 'Daily lists generated · sending them to the player';
            }
            const dueMs = timeMs(run.queued_scheduled_time);
            if (dueMs !== null && dueMs > nowMs) {
                return `${verb} waits until ${clock(run.queued_scheduled_time)}`;
            }
            return `${verb} queued ${clock(run.started_at)} · sends within a minute`;
        }
        case 'received':
            return `${verb} sent ${clock(run.command?.issued_at)}${checkedIn}`;
        case 'downloading':
            return `The player received ${verb} ${clock(run.command?.acknowledged_at)} · getting its lists${checkedIn}`;
        case 'up_to_date':
            return `Downloading tracks since ${clock(run.command?.delivered_at)}${checkedIn}`;
        case 'reported':
            return `Tracks downloaded ${clock(run.command?.completed_at)} · waiting for the player's file list${checkedIn}`;
    }
}

/** The last time the player answered this run, or the run's start when it never has. */
function lastAnswerAt(run: PlayerScheduleRun): string {
    const answers = [run.command?.acknowledged_at, run.command?.delivered_at, run.command?.completed_at].filter(
        (value): value is string => timeMs(value) !== null
    );
    if (answers.length === 0) {
        return run.started_at;
    }
    return answers.reduce((latest, value) => ((timeMs(value) ?? 0) > (timeMs(latest) ?? 0) ? value : latest));
}

/**
 * The progress bar for the run the server reports, or null when none is in
 * progress.
 *
 * A step is done when it, or any later step, has evidence. So a pull recorded
 * without its flush still marks Received done. The current step is the first
 * one with no evidence. The run stops being followed when the report is newer
 * than the run, when the generation fails, or when the server stops returning
 * it, 15 minutes after it started by the server's clock.
 */
export function deriveScheduleProgress(
    input: ScheduleProgressInput,
    nowMs: number,
    options: ScheduleProgressOptions = {}
): ScheduleProgress | null {
    const run = input.run;
    if (!run) {
        return null;
    }
    const keys = run.kind === 'generate' ? GENERATE_STEPS : RELOAD_STEPS;
    let lastReached = -1;
    keys.forEach((key, index) => {
        if (hasEvidence(key, input, run)) {
            lastReached = index;
        }
    });
    const currentIndex = lastReached + 1 < keys.length ? lastReached + 1 : null;
    const currentStep = currentIndex === null ? null : keys[currentIndex]!;

    const failed =
        run.kind === 'generate' &&
        run.attempt?.playlist_generation_status_id === PLAYLIST_GENERATION_STATUS.FAILED;
    // Only the server ends a run, by its own clock, so a viewer's clock that is off cannot.
    const timedOut = !failed && currentStep !== null && options.serverEnded === true;

    const steps: ScheduleStep[] = keys.map((key, index) => {
        let status: ScheduleStepStatus = 'pending';
        if (index <= lastReached) {
            status = 'done';
        } else if (index === currentIndex) {
            status = failed && key === 'generating' ? 'failed' : 'current';
        }
        return { key, label: STEP_LABELS[key], status };
    });

    let detail: string;
    if (failed) {
        detail = 'Generating failed';
    } else if (currentStep === null) {
        detail = `The player reported its lists ${clock(input.report.reported_at)}`;
    } else if (timedOut) {
        detail = `No answer from the player since ${clock(lastAnswerAt(run))}`;
    } else {
        detail = runningDetail(currentStep, run, nowMs, options.lastCheckinAt);
    }

    return {
        kind: run.kind,
        steps,
        currentStep,
        detail,
        failed,
        timedOut,
        running: !failed && !timedOut && currentStep !== null,
    };
}

// ---------------------------------------------------------------------------
// Status line
// ---------------------------------------------------------------------------

export interface ScheduleStatusLine {
    updatedText: string;
    badge: 'updating' | 'out_of_date' | 'up_to_date' | 'unknown';
    badgeLabel: string;
}

/**
 * "Lists updated Tue 10/27, 7:05 AM" and the badge on its right.
 *
 * Either kind of list being out of date makes the player out of date. The daily
 * lists' "unknown" only means the player holds none, which many zones never use,
 * so it does not stop the player reading Up to date. The custom lists' "unknown"
 * means the report has no time, so nothing can be said.
 */
export function scheduleStatusLine(
    report: {
        report: Pick<PlayerScheduleReport['report'], 'reported_at'>;
        custom: Pick<PlayerScheduleReport['custom'], 'state'>;
        daily: Pick<PlayerScheduleReport['daily'], 'state'>;
    },
    updating: boolean
): ScheduleStatusLine {
    const reportedAt = report.report.reported_at ? dayjs(report.report.reported_at) : null;
    const updatedText =
        reportedAt && reportedAt.isValid()
            ? `Lists updated ${reportedAt.format('ddd M/D, h:mm A')}`
            : 'Lists updated: time not recorded';
    if (updating) {
        return { updatedText, badge: 'updating', badgeLabel: 'Updating…' };
    }
    if (report.custom.state === 'out_of_date' || report.daily.state === 'out_of_date') {
        return { updatedText, badge: 'out_of_date', badgeLabel: 'Out of date' };
    }
    if (report.custom.state === 'unknown') {
        return { updatedText, badge: 'unknown', badgeLabel: "Can't tell" };
    }
    return { updatedText, badge: 'up_to_date', badgeLabel: 'Up to date' };
}

// ---------------------------------------------------------------------------
// Daily lists
// ---------------------------------------------------------------------------

/** "Mon 10/26" for "2026-10-26", read as a calendar day, not a UTC instant. */
export function formatDailyDate(date: string): string {
    const moment = dayjs(date);
    return moment.isValid() ? moment.format('ddd M/D') : date;
}

const MISSING_DAYS_SHOWN = 3;

/** "Mon 10/26 – Tue 11/10" over "16 lists · no days missing". */
export function dailyRangeSummary(
    daily: Pick<PlayerScheduleReport['daily'], 'first_date' | 'last_date' | 'missing_dates' | 'files'>
): { range: string | null; detail: string } {
    if (daily.files.length === 0 || !daily.first_date || !daily.last_date) {
        return { range: null, detail: 'No daily lists on the player' };
    }
    const range =
        daily.first_date === daily.last_date
            ? formatDailyDate(daily.first_date)
            : `${formatDailyDate(daily.first_date)} – ${formatDailyDate(daily.last_date)}`;
    const count = `${daily.files.length} ${daily.files.length === 1 ? 'list' : 'lists'}`;
    if (daily.missing_dates.length === 0) {
        return { range, detail: `${count} · no days missing` };
    }
    const named = daily.missing_dates.slice(0, MISSING_DAYS_SHOWN).map(formatDailyDate).join(', ');
    const more = daily.missing_dates.length - MISSING_DAYS_SHOWN;
    return { range, detail: `${count} · missing ${named}${more > 0 ? ` and ${more} more` : ''}` };
}

const DAILY_REASON_SUBJECT: Record<DailyListReason['kind'], (name: string) => string> = {
    template: (name) => `The ${name} template`,
    template_day: (name) => `The ${name} template day`,
    template_day_insert: (name) => `The ${name} insert in a template day`,
};

/** "The Default template changed · Oct 29", worded like the Sync popover's lines. */
export function dailyReasonText(reason: DailyListReason, nowMs: number): string {
    const when = formatSyncTime(reason.updated_at, nowMs);
    const subject = DAILY_REASON_SUBJECT[reason.kind]?.(reason.name) ?? `The ${reason.name} template`;
    return when ? `${subject} changed · ${when}` : `${subject} changed`;
}

// ---------------------------------------------------------------------------
// Custom list schedules
// ---------------------------------------------------------------------------

/**
 * When a schedule plays, from the per-type columns the player reads.
 * A repeating one reads "Su M W · 6am-2pm · from 15 Nov 2026"; a dated one
 * reads "Sun 1 Nov 2026 9am – Sat 26 Dec 2026 11:59pm".
 */
export function scheduleWhenText(row: Pick<PlayerScheduleRow, 'starts' | 'ends'>): string {
    const { starts, ends } = row;
    if (starts.day_ids.length > 0) {
        const days = [...starts.day_ids]
            .sort((left, right) => left - right)
            .map((dayId) => DAY_TEXT_LABELS[dayId] ?? String(dayId))
            .join(' ');
        const startDate = formatScheduleShortDate(starts.date);
        const endDate = formatScheduleShortDate(ends.date);
        let dates: string | null = null;
        if (startDate && endDate) {
            dates = `${startDate} – ${endDate}`;
        } else if (startDate) {
            dates = `from ${startDate}`;
        } else if (endDate) {
            dates = `until ${endDate}`;
        }
        return [days, formatTimeRange(starts.time, ends.time), dates].filter(Boolean).join(' · ');
    }
    const startText = [formatScheduleDay(starts.date), formatClockTime(starts.time)].filter(Boolean).join(' ');
    const endText = [formatScheduleDay(ends.date), formatClockTime(ends.time)].filter(Boolean).join(' ');
    if (startText && endText) {
        return `${startText} – ${endText}`;
    }
    if (startText) {
        return `from ${startText}`;
    }
    return endText ? `until ${endText}` : '';
}
